import * as React from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import PageHeader from "theme/components/common/PageHeader";
import DateTimeRangeSelector, {
  usePersistentDateTimeRange,
} from "components/dates/DateTimeRangeSelector";
import apiClient from "api";
import debounce from "lodash/debounce";
import { toast } from "react-toastify";
import {
  nativeAppBridge,
  LocalReportSummary,
  LocalReportPayload,
} from "utils/nativeAppBridge";
import DefaultTooltip from "components/common/DefaultTooltip";
import { gettext } from "i18n";
import { REPORT_TYPE_KEY_TO_DISPLAY } from "./MeasurementStationPage";
import { capitalize } from "utils/formatting";

import "./OfflineReportsPage.scss";

interface OfflineReport {
  uuid: string;
  stationId: number | null;
  stationName: string | null;
  organizationName: string | null;
  reportType: string;
  status: "draft" | "finished";
  responsiblePerson: string | null;
  timestamp: string | null;
  createdAt: string;
  updatedAt: string;
  attachmentCount: number;
}

const translateReportType = (reportTypeKey: string): string => {
  const displayName =
    REPORT_TYPE_KEY_TO_DISPLAY[reportTypeKey] ?? reportTypeKey;
  return gettext(displayName);
};

const formatDateTime = (iso: string | number | null) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
};

const useOfflineReports = ({
  stationQuery,
  status,
  dateFrom,
  dateTo,
  filterByTimestamp,
}: {
  stationQuery: string;
  status: string;
  dateFrom: Date | undefined;
  dateTo: Date | undefined;
  filterByTimestamp: boolean;
}) => {
  const [reports, setReports] = React.useState<OfflineReport[]>([]);
  const [loading, setLoading] = React.useState(false);

  const fetch = React.useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (stationQuery) params.stationQuery = stationQuery;
      if (status) params.status = status;
      if (dateFrom) params.dateFrom = dateFrom.toISOString();
      if (dateTo) {
        const dateToEndOfDay = new Date(dateTo);
        dateToEndOfDay.setHours(23, 59, 59, 999);
        params.dateTo = dateToEndOfDay.toISOString();
      }
      if (filterByTimestamp) params.filterByTimestamp = "true";

      const qs = new URLSearchParams(params).toString();
      const result = await apiClient.request(
        `/organization-admin/offline-report/list${qs ? `?${qs}` : ""}`,
        { method: "GET" },
      );
      setReports(result.reports ?? []);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load reports");
    } finally {
      setLoading(false);
    }
  }, [stationQuery, status, dateFrom, dateTo, filterByTimestamp]);

  React.useEffect(() => {
    fetch();
  }, [fetch]);

  return { reports, loading, refetch: fetch };
};

interface UploadModalProps {
  onClose: () => void;
  onUploadComplete: () => void;
}

const UploadModal: React.FC<UploadModalProps> = ({
  onClose,
  onUploadComplete,
}) => {
  const [phase, setPhase] = React.useState<
    "loading-list" | "picking" | "uploading" | "error"
  >("loading-list");
  const [localReports, setLocalReports] = React.useState<LocalReportSummary[]>(
    [],
  );
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [uploadingId, setUploadingId] = React.useState<string | null>(null);

  React.useEffect(() => {
    nativeAppBridge
      .requestLocalReports()
      .then((reports) => {
        setLocalReports(reports);
        setPhase("picking");
      })
      .catch((err) => {
        console.error("[UploadModal] Failed to fetch local reports:", err);
        setErrorMessage(
          typeof err === "string"
            ? err
            : "Failed to retrieve reports from the mobile app.",
        );
        setPhase("error");
      });
  }, []);

  const handleUpload = async (report: LocalReportSummary) => {
    setUploadingId(report.id);
    setPhase("uploading");

    let payload: LocalReportPayload;
    try {
      payload = await nativeAppBridge.requestReportPayload(report.id);
    } catch (err) {
      console.error("[UploadModal] Failed to fetch report payload:", err);
      toast.error(
        "Failed to read report data from the mobile app. Please try again.",
      );
      setUploadingId(null);
      setPhase("picking");
      return;
    }

    try {
      await apiClient.request("/organization-admin/offline-report/upload", {
        method: "POST",
        data: {
          uuid: payload.id,
          stationId: payload.stationId,
          reportType: payload.reportType,
          status: payload.status,
          responsiblePerson: payload.responsiblePerson,
          timestamp: payload.timestamp,
          data: payload.data,
          attachments: payload.attachments.map((a) => ({
            filename: a.filename,
            mimeType: a.mimeType,
            base64: a.base64,
            isAdminOnly: a.isAdminOnly,
          })),
        },
      });
      toast.success("Report uploaded successfully.");
      onUploadComplete();
      onClose();
    } catch (err) {
      console.error("[UploadModal] Upload failed:", err);
      toast.error(
        err instanceof Error
          ? err.message
          : "Upload failed. Please try again later.",
      );
      setUploadingId(null);
      setPhase("picking");
    }
  };

  return (
    <Modal show onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Upload report from device</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {phase === "loading-list" && (
          <div className="OfflineReportsPage__modal-status">
            <Spinner animation="border" size="sm" className="me-2" />
            Retrieving reports from the mobile app…
          </div>
        )}

        {phase === "uploading" && (
          <div className="OfflineReportsPage__modal-status">
            <Spinner animation="border" size="sm" className="me-2" />
            Uploading report…
          </div>
        )}

        {phase === "error" && (
          <div className="OfflineReportsPage__modal-error">
            {errorMessage ?? "An unexpected error occurred."}
          </div>
        )}

        {phase === "picking" && localReports.length === 0 && (
          <div className="OfflineReportsPage__empty">
            No reports found on this device.
          </div>
        )}

        {phase === "picking" && localReports.length > 0 && (
          <div className="OfflineReportsPage__report-list">
            {localReports.map((report) => (
              <div key={report.id} className="OfflineReportsPage__report-card">
                <div className="OfflineReportsPage__report-card-main">
                  <div className="OfflineReportsPage__report-card-station">
                    {report.stationName ?? "—"}
                    {report.organizationName && (
                      <span className="OfflineReportsPage__report-card-org">
                        {" "}
                        ({report.organizationName})
                      </span>
                    )}
                  </div>
                  <div className="OfflineReportsPage__report-card-meta">
                    <span>{translateReportType(report.reportType)}</span>
                    <span
                      className={`OfflineReportsPage__badge OfflineReportsPage__badge--${report.status}`}
                    >
                      {gettext(capitalize(report.status))}
                    </span>
                  </div>
                  <div className="OfflineReportsPage__report-card-date">
                    {formatDateTime(report.updatedAt)}
                  </div>
                </div>
                <div className="OfflineReportsPage__report-card-action">
                  <Button
                    size="sm"
                    variant="primary"
                    disabled={uploadingId === report.id}
                    onClick={() => handleUpload(report)}
                  >
                    Upload
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

interface OfflineReportsPageProps {
  permissions?: string[];
}

const OfflineReportsPage: React.FC<OfflineReportsPageProps> = () => {
  const [dateRange, setDateRange] = usePersistentDateTimeRange();
  const [startDate, endDate] = dateRange;

  const [stationQuery, setStationQuery] = React.useState("");
  const [debouncedStationQuery, setDebouncedStationQuery] = React.useState("");
  const debouncedSetQuery = React.useMemo(
    () => debounce((v: string) => setDebouncedStationQuery(v), 400),
    [],
  );
  React.useEffect(() => {
    debouncedSetQuery(stationQuery);
    return () => debouncedSetQuery.cancel();
  }, [stationQuery, debouncedSetQuery]);

  const [statusFilter, setStatusFilter] = React.useState("");
  const [filterByTimestamp, setFilterByTimestamp] = React.useState(false);
  const [showUploadModal, setShowUploadModal] = React.useState(false);
  const [isNativeConnected, setIsNativeConnected] = React.useState(
    nativeAppBridge.isNativeConnected,
  );
  React.useEffect(() => {
    nativeAppBridge.onConnected(() => setIsNativeConnected(true));
  }, []);

  const { reports, loading, refetch } = useOfflineReports({
    stationQuery: debouncedStationQuery,
    status: statusFilter,
    dateFrom: startDate,
    dateTo: endDate,
    filterByTimestamp,
  });
  const navigate = useNavigate();

  return (
    <div className="OfflineReportsPage">
      <PageHeader title="Reports" className="mb-3" />

      <Card className="mb-3">
        <Card.Body>
          <Row className="g-3 mb-3">
            <Col xs={12}>
              <Form.Label className="fw-semibold">Date range</Form.Label>
              <DateTimeRangeSelector
                range={dateRange}
                setRange={setDateRange}
                quickChoices={[
                  ["7 d", 7 * 24 * 60 * 60 * 1000],
                  ["30 d", 30 * 24 * 60 * 60 * 1000],
                  ["90 d", 90 * 24 * 60 * 60 * 1000],
                ]}
              />
              <Form.Check
                type="switch"
                id="filter-by-timestamp"
                className="mt-2"
                label={gettext("Filter by visit time")}
                checked={filterByTimestamp}
                onChange={(e) => setFilterByTimestamp(e.target.checked)}
              />
              {!filterByTimestamp && (
                <Form.Text muted>
                  Filtering by upload date. Enable to filter by the visit time
                  recorded by the administrator instead.
                </Form.Text>
              )}
            </Col>
          </Row>
          <Row className="g-3 align-items-end">
            <Col xs={12} md={4}>
              <Form.Label className="fw-semibold">Station</Form.Label>
              <Form.Control
                type="text"
                placeholder={gettext(
                  "Search by station name, identifier or organization",
                )}
                value={stationQuery}
                onChange={(e) => setStationQuery(e.target.value)}
              />
            </Col>
            <Col xs={12} md={4}>
              <Form.Label className="fw-semibold">Status</Form.Label>
              <Form.Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">{gettext("All statuses")}</option>
                <option value="draft">{gettext("Draft")}</option>
                <option value="finished">{gettext("Finished")}</option>
              </Form.Select>
            </Col>
            <Col xs={12} md={4} className="d-flex justify-content-end">
              <div className="d-flex align-items-center gap-2">
                <Button
                  variant="primary"
                  disabled={!isNativeConnected}
                  onClick={() => setShowUploadModal(true)}
                >
                  Upload report
                </Button>
                {!isNativeConnected && (
                  <DefaultTooltip
                    popBody={
                      <p>
                        This feature is only available when using the mobile
                        app.
                      </p>
                    }
                  />
                )}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          {loading ? (
            <div className="OfflineReportsPage__spinner">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading reports…
            </div>
          ) : reports.length === 0 ? (
            <div className="OfflineReportsPage__empty">No reports found.</div>
          ) : (
            <>
              <Table hover responsive className="d-none d-md-table">
                <thead>
                  <tr>
                    <th>Station</th>
                    <th>{gettext("Organization")}</th>
                    <th>Report type</th>
                    <th>Responsible person</th>
                    <th>Visit time</th>
                    <th>Status</th>
                    <th>Attachments</th>
                    <th>Last updated</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((report) => (
                    <tr
                      key={report.uuid}
                      className="OfflineReportsPage__row OfflineReportsPage__row--clickable"
                      onClick={() => navigate(`/admin/reports/${report.uuid}`)}
                    >
                      <td>{report.stationName ?? report.stationId ?? "—"}</td>
                      <td>{report.organizationName ?? "—"}</td>
                      <td>{translateReportType(report.reportType)}</td>
                      <td>{report.responsiblePerson ?? "—"}</td>
                      <td>{formatDateTime(report.timestamp)}</td>
                      <td>
                        <span
                          className={`OfflineReportsPage__badge OfflineReportsPage__badge--${report.status}`}
                        >
                          {gettext(capitalize(report.status))}
                        </span>
                      </td>
                      <td>{report.attachmentCount || "—"}</td>
                      <td>{formatDateTime(report.updatedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              <div className="OfflineReportsPage__report-list d-md-none">
                {reports.map((report) => (
                  <div
                    key={report.uuid}
                    className="OfflineReportsPage__report-card OfflineReportsPage__report-card--clickable"
                    onClick={() => navigate(`/admin/reports/${report.uuid}`)}
                  >
                    <div className="OfflineReportsPage__report-card-main">
                      <div className="OfflineReportsPage__report-card-station">
                        {report.stationName ?? report.stationId ?? "—"}
                      </div>
                      <div className="OfflineReportsPage__report-card-meta">
                        <span>{report.organizationName ?? "—"}</span>
                        <span>{translateReportType(report.reportType)}</span>
                        {report.responsiblePerson && (
                          <span>{report.responsiblePerson}</span>
                        )}
                        <span
                          className={`OfflineReportsPage__badge OfflineReportsPage__badge--${report.status}`}
                        >
                          {gettext(capitalize(report.status))}
                        </span>
                      </div>
                      <div className="OfflineReportsPage__report-card-date">
                        {formatDateTime(report.timestamp)}
                        {report.attachmentCount > 0 && (
                          <span>
                            {" "}
                            · {report.attachmentCount} attachment
                            {report.attachmentCount !== 1 ? "s" : ""}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      {showUploadModal && (
        <UploadModal
          onClose={() => setShowUploadModal(false)}
          onUploadComplete={refetch}
        />
      )}
    </div>
  );
};

export default OfflineReportsPage;
